import type { Item, List, Status } from "~/types/api";
import { useTabWindowsContext } from "../../../../tab-windows/hooks/use-tab-windows-context";
import { groupItemsByStatus } from "../../../../tab-windows/utils/window-helpers";

type Props = {
  windowId: string;
  lists: List[];
  statuses: Status[];
  allItems: Item[];
  title?: string;
};

type ReturnProps = {
  handleOpenInNewWindow: (listId: number) => void;
  handleToggleListFilter: (listId: number) => void;
  handleClearFilter: () => void;
};

export const useListsFilter = ({
  windowId,
  lists,
  statuses,
  allItems,
  title,
}: Props): ReturnProps => {
  const { addWindow, updateItems, updateWindowState, getWindow } = useTabWindowsContext();

  const getStatusCodesForLists = (listIds: number[]): Set<number> => {
    const allStatusCodes = new Set<number>();
    listIds.forEach((listId) => {
      const list = lists.find((l) => l.id === listId);
      if (list?.statuses && list.statuses.length > 0) {
        list.statuses.forEach((s) => allStatusCodes.add(s.code));
      }
    });
    return allStatusCodes;
  };

  const getFilteredItemsByLists = (listIds: number | number[]): Item[] => {
    const ids = Array.isArray(listIds) ? listIds : [listIds];
    const statusCodes = getStatusCodesForLists(ids);
    if (statusCodes.size === 0) return [];
    return allItems.filter((item) => statusCodes.has(item.statusCode));
  };

  const handleOpenInNewWindow = (listId: number) => {
    const list = lists.find((l) => l.id === listId);
    if (!list) return;

    const filteredItems = getFilteredItemsByLists(listId);
    const newWindowId = `window-list-${listId}`;

    const windowTitle = title || "List";
    const windowSubtitle = list.name;

    const currentWindow = getWindow(windowId);
    const inheritedTaskIds = currentWindow?.state.taskIds || [];

    const options = {
      subtitle: windowSubtitle,
      taskIds: inheritedTaskIds,
      appliedListIds: [listId],
      insertAfterWindowId: windowId,
    };

    if (statuses.length > 0) {
      const groupedItems = groupItemsByStatus(filteredItems, statuses);
      addWindow(groupedItems, newWindowId, windowTitle, options);
    } else {
      addWindow(filteredItems, newWindowId, windowTitle, options);
    }
  };

  const handleClearFilter = () => {
    const currentWindow = getWindow(windowId);
    if (!currentWindow?.originalItems) return;

    updateItems(currentWindow.originalItems, (window) => window.id === windowId);
    updateWindowState(windowId, {
      ...currentWindow.state,
      appliedListIds: undefined,
      subtitle: undefined,
    });
  };

  const handleToggleListFilter = (listId: number) => {
    const currentWindow = getWindow(windowId);
    const isCurrentlyApplied = currentWindow?.state.appliedListIds?.[0] === listId;

    if (isCurrentlyApplied) {
      handleClearFilter();
      return;
    }

    const list = lists.find((l) => l.id === listId);
    if (!list) return;

    const filteredItems = getFilteredItemsByLists([listId]);

    if (statuses.length > 0) {
      const groupedItems = groupItemsByStatus(filteredItems, statuses);
      updateItems(groupedItems, (window) => window.id === windowId);
    } else {
      updateItems([...filteredItems], (window) => window.id === windowId);
    }

    if (currentWindow) {
      updateWindowState(windowId, {
        ...currentWindow.state,
        appliedListIds: [listId],
        subtitle: list.name,
      });
    }
  };

  return {
    handleOpenInNewWindow,
    handleToggleListFilter,
    handleClearFilter,
  };
};
